import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { toast } from "react-hot-toast";
import supabase from "../SupaCredentials";

const useAuthManager = () => {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userDataLoading, setUserDataLoading] = useState(false);
  const [error, setError] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const [isActiveUser, setIsActiveUser] = useState(false);

  // Ref para controlar si el componente está montado
  const isMounted = useRef(true);
  // Ref para dar ID único a cada instancia para logs
  const instanceIdRef = useRef(Math.random().toString(36).substring(7));
  // Ref para evitar múltiples inicializaciones
  const initializationPromise = useRef(null);

  // Memoizar fetchUserData para evitar recreación
  const fetchUserData = useCallback(async (userId) => {
    const instanceId = instanceIdRef.current;
    if (!userId || !isMounted.current) return null;

    setUserDataLoading(true);
    setError(null);

    try {
      let attempts = 0;
      let fetchedUserData = null;
      let fetchError = null;

      while (attempts < 2 && !fetchedUserData) {
        try {
          const { data, error: currentError } = await supabase
            .from("user_data")
            .select("*")
            .eq("id", userId)
            .single();

          if (currentError && currentError.code !== "PGRST116") {
            fetchError = currentError;
            throw currentError;
          } else if (!data && currentError?.code === "PGRST116") {
            console.warn(
              `useAuthManager[${instanceId}]: No user_data found for user ${userId} (PGRST116).`
            );
            fetchedUserData = null;
            break;
          } else if (!data && !currentError) {
            console.warn(
              `useAuthManager[${instanceId}]: No user_data found for user ${userId}, but no error.`
            );
            fetchedUserData = null;
            break;
          } else {
            fetchedUserData = data;
            break;
          }
        } catch (e) {
          fetchError = e;
          attempts++;
          if (attempts < 2)
            await new Promise((resolve) => setTimeout(resolve, 500 * attempts));
        }
      }

      if (!fetchedUserData && fetchError && fetchError.code !== "PGRST116") {
        throw fetchError;
      }

      if (isMounted.current) {
        setUserData(fetchedUserData);
      }
      return fetchedUserData;
    } catch (error) {
      console.error(
        `useAuthManager[${instanceId}]: Error final al obtener datos de usuario:`,
        error.message
      );
      if (isMounted.current) {
        setError("Error al cargar datos del perfil.");
        setUserData(null);
      }
      return null;
    } finally {
      if (isMounted.current) {
        setUserDataLoading(false);
      }
    }
  }, []);

  // Memoizar initAuth para evitar recreación y múltiples ejecuciones
  const initAuth = useCallback(async () => {
    const instanceId = instanceIdRef.current;

    // Si ya se está inicializando, retornar la promesa existente
    if (initializationPromise.current) {
      return initializationPromise.current;
    }

    if (initialized || !isMounted.current) {
      if (initialized && loading) setLoading(false);
      return;
    }

    // Crear promesa de inicialización
    initializationPromise.current = (async () => {
      if (isMounted.current) {
        setLoading(true);
        setError(null);
      } else {
        return;
      }

      try {
        const { data: sessionData, error: sessionError } =
          await supabase.auth.getSession();

        if (!isMounted.current) {
          return;
        }

        if (sessionError) {
          console.error(
            `useAuthManager[${instanceId}]: Error al obtener sesión inicial:`,
            sessionError
          );
          setSession(null);
          setUser(null);
          setUserData(null);
          setIsActiveUser(false);
        } else if (sessionData?.session) {
          setSession(sessionData.session);
          setUser(sessionData.session.user);
          setIsActiveUser(true);
          await fetchUserData(sessionData.session.user.id);
        } else {
          setSession(null);
          setUser(null);
          setUserData(null);
          setIsActiveUser(false);
        }
      } catch (err) {
        console.error(
          `useAuthManager[${instanceId}]: Error crítico durante initAuth:`,
          err
        );
        if (isMounted.current) {
          setError(err.message);
          setSession(null);
          setUser(null);
          setUserData(null);
          setIsActiveUser(false);
        }
      } finally {
        if (isMounted.current) {
          setInitialized(true);
          setLoading(false);
        }
        // Limpiar la promesa de inicialización
        initializationPromise.current = null;
      }
    })();

    return initializationPromise.current;
  }, [fetchUserData, initialized, loading]);

  // Memoizar login para evitar recreación
  const login = useCallback(async (email, password) => {
    const instanceId = instanceIdRef.current;
    if (!isMounted.current)
      return { success: false, error: "Componente desmontado" };

    setLoading(true);
    setError(null);

    try {
      const { data, error: loginError } =
        await supabase.auth.signInWithPassword({ email, password });
      if (loginError) throw loginError;

      await new Promise((resolve) => setTimeout(resolve, 100));

      if (isMounted.current) {
        const name = data.user?.first_name || "Usuario";
        toast.success(`¡Hola de nuevo, ${name}!`, {
          duration: 3000,
          position: "top-center",
          style: {
            borderRadius: "10px",
            background: "#222",
            color: "#fff",
          },
        });
      }
      return { success: true, user: data.user };
    } catch (err) {
      console.error(`useAuthManager[${instanceId}]: Error en login:`, err);
      const message = err.message || "Correo o contraseña incorrectos";
      if (isMounted.current) {
        setError(message);
        toast.error(`Error al iniciar sesión: ${message}`, {
          duration: 4000,
          position: "top-center",
        });
      }
      return { success: false, error: message };
    } finally {
      if (isMounted.current) setLoading(false);
    }
  }, []);

  // Memoizar logout para evitar recreación
  const logout = useCallback(async () => {
    const instanceId = instanceIdRef.current;
    if (!isMounted.current) return false;

    setLoading(true);
    setError(null);

    try {
      const { error: logoutError } = await supabase.auth.signOut();
      if (logoutError) throw logoutError;

      if (isMounted.current) {
        toast.success("Sesión cerrada correctamente", {
          duration: 3000,
          position: "top-center",
          style: {
            background: "#222",
            color: "#fff",
          },
        });
      }
      return true;
    } catch (err) {
      console.error(`useAuthManager[${instanceId}]: Error en logout:`, err);
      const message = err.message || "Error al cerrar sesión";
      if (isMounted.current) {
        setError(message);
        toast.error(message, { duration: 3000, position: "top-center" });
      }
      return false;
    } finally {
      if (isMounted.current) setLoading(false);
    }
  }, []);

  // Cambiar el registerUser para hacer UPDATE en lugar de INSERT
  // Memoizar registerUser para evitar recreación
  const registerUser = useCallback(
    async (email, password, additionalData = {}) => {
      const instanceId = instanceIdRef.current;
      if (!isMounted.current)
        return { success: false, error: "Componente desmontado" };

      setLoading(true);
      setError(null);

      try {
        // 1. Crear usuario en auth
        const { data, error: signUpError } = await supabase.auth.signUp({
          email,
          password,
        });

        if (signUpError) throw signUpError;
        if (!data.user)
          throw new Error("No se pudo crear el usuario en Supabase Auth.");

        // 2. Filtrar campos válidos para la tabla user_data (remover campos que no existen)
        const { phone_raw, document_value_raw, ...validData } = additionalData;

        // 3. Esperar un momento para que el trigger termine (éxito o fallo)
        await new Promise((resolve) => setTimeout(resolve, 1000));

        // 4. Verificar si existe el registro en user_data
        const { data: existingUser, error: checkError } = await supabase
          .from("user_data")
          .select("id")
          .eq("id", data.user.id)
          .single();

        if (checkError && checkError.code !== "PGRST116") {
          console.warn(
            `useAuthManager[${instanceId}]: Error verificando usuario existente:`,
            checkError
          );
        }

        if (existingUser) {
          // El registro existe (trigger funcionó), actualizar con datos adicionales
          const { error: updateError } = await supabase
            .from("user_data")
            .update(validData)
            .eq("id", data.user.id);

          if (updateError) {
            console.error(
              `useAuthManager[${instanceId}]: Error actualizando datos:`,
              updateError
            );
            throw new Error(
              `Error al guardar datos adicionales: ${updateError.message}`
            );
          }
        } else {
          // El registro no existe (trigger falló), crear uno nuevo completo
          const { error: insertError } = await supabase
            .from("user_data")
            .insert({
              id: data.user.id,
              email: data.user.email,
              has_account: true,
              ...validData,
            });

          if (insertError) {
            console.error(
              `useAuthManager[${instanceId}]: Error insertando datos:`,
              insertError
            );
            throw new Error(
              `Error al crear perfil de usuario: ${insertError.message}`
            );
          }
        }

        if (isMounted.current) {
          toast.success("¡Cuenta creada! Revisa tu correo para confirmar.", {
            duration: 5000,
            position: "top-center",
          });
        }
        return { success: true, user: data.user };
      } catch (err) {
        console.error(`useAuthManager[${instanceId}]: Error en registro:`, err);
        const message = err.message || "No se pudo registrar la cuenta.";
        if (isMounted.current) {
          setError(message);
          toast.error(`Error al registrar: ${message}`, {
            duration: 4000,
            position: "top-center",
          });
        }
        return { success: false, error: message };
      } finally {
        if (isMounted.current) {
          setLoading(false);
        }
      }
    },
    []
  );

  // Memoizar clearError para evitar recreación
  const clearError = useCallback(() => {
    if (isMounted.current) setError(null);
  }, []);

  // Memoizar combinedUser para evitar recreación constante
  const combinedUser = useMemo(() => {
    if (!user) return null;
    return { ...user, userData: userData };
  }, [user, userData]);

  // Memoizar el valor de loading
  const isLoadingMemo = useMemo(
    () => loading || userDataLoading,
    [loading, userDataLoading]
  );

  // Memoizar todo el objeto de retorno para evitar re-renders
  const contextValue = useMemo(
    () => ({
      user: combinedUser,
      session,
      loading: isLoadingMemo,
      error,
      isActiveUser,
      initialized,
      login,
      logout,
      registerUser,
      fetchUserData,
      clearError,
    }),
    [
      combinedUser,
      session,
      isLoadingMemo,
      error,
      isActiveUser,
      initialized,
      login,
      logout,
      registerUser,
      fetchUserData,
      clearError,
    ]
  );

  // Efecto para llamar a initAuth (optimizado)
  useEffect(() => {
    const instanceId = instanceIdRef.current;
    isMounted.current = true;

    if (!initialized) {
      initAuth();
    } else {
      if (loading) {
        setLoading(false);
      }
    }

    return () => {
      isMounted.current = false;
      // Limpiar promesa de inicialización si el componente se desmonta
      if (initializationPromise.current) {
        initializationPromise.current = null;
      }
    };
  }, []); // Dependencias vacías para ejecutar solo una vez

  // Efecto para onAuthStateChange (optimizado)
  // Efecto para onAuthStateChange (OPTIMIZADO)
  useEffect(() => {
    const instanceId = instanceIdRef.current;
    let unsubscribe = () => {};

    if (initialized) {
      const { data: authListener } = supabase.auth.onAuthStateChange(
        async (event, newSession) => {
          if (!isMounted.current || !initialized) {
            return;
          }

          // 🚀 FILTRAR EVENTOS INNECESARIOS
          const currentUser = newSession?.user || null;
          const previousUserId = user?.id;
          const currentUserId = currentUser?.id;

          // Ignorar TOKEN_REFRESHED si el usuario no cambió realmente
          if (event === "TOKEN_REFRESHED" && currentUserId === previousUserId) {
            console.log(
              `🔄 useAuthManager[${instanceId}]: TOKEN_REFRESHED ignorado - mismo usuario`
            );
            return;
          }

          // 🚀 SOLO ACTUALIZAR ESTADO SI HAY CAMBIOS REALES
          const sessionChanged =
            JSON.stringify(session) !== JSON.stringify(newSession);
          const userChanged = currentUserId !== previousUserId;

          if (sessionChanged) {
            setSession(newSession);
          }

          if (userChanged || !currentUser !== !user) {
            setUser(currentUser);
            setIsActiveUser(!!currentUser);
          }

          // 🚀 FETCH USER DATA SOLO CUANDO SEA NECESARIO
          if (currentUser && userChanged) {
            console.log(
              `📥 useAuthManager[${instanceId}]: Usuario cambió, fetching data`
            );
            await fetchUserData(currentUser.id);
          } else if (currentUser && event === "USER_UPDATED") {
            console.log(
              `📥 useAuthManager[${instanceId}]: Usuario actualizado, fetching data`
            );
            await fetchUserData(currentUser.id);
          } else if (!currentUser && previousUserId) {
            console.log(
              `📤 useAuthManager[${instanceId}]: Usuario deslogueado, limpiando data`
            );
            setUserData(null);
            if (isMounted.current) setUserDataLoading(false);
          }
        }
      );

      unsubscribe = () => {
        if (authListener?.subscription) {
          authListener.subscription.unsubscribe();
        }
      };
    }

    return unsubscribe;
  }, [initialized]); // 🚀 DEPENDENCIAS OPTIMIZADAS - Solo 'initialized'

  return contextValue;
};

export default useAuthManager;
